import React, { useEffect, useState, useCallback } from "react";
import staffApi from "@/lib/staffApi";
import { PageHead } from "./DashboardLayout";
import { money } from "@/data/site";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { X } from "lucide-react";

export default function Inventory() {
  const [data, setData] = useState({ locations: [], rows: [] });
  const [locationId, setLocationId] = useState("");
  const [adjust, setAdjust] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await staffApi.get("/admin/inventory", { params: locationId ? { location_id: locationId } : {} });
    setData(data);
  }, [locationId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div data-testid="inventory-page">
      <PageHead kicker="Stock Control" title="Inventory"
        action={
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)} data-testid="location-filter"
            className="border border-white/15 bg-ink px-4 py-2 text-xs uppercase tracking-widest text-cream outline-none focus:border-gold">
            <option value="" className="bg-ink">All locations</option>
            {data.locations.map((l) => <option key={l.id} value={l.id} className="bg-ink">{l.name}</option>)}
          </select>
        } />

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[720px] text-sm" data-testid="inventory-table">
          <thead><tr className="bg-ink text-gold/80">{["Product", "SKU", "Colour", "Size", "Price", "Total Stock", ""].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.variant_id} className={`border-t border-white/10 ${r.low ? "bg-yellow-600/5" : ""}`} data-testid={`inventory-row-${r.variant_id}`}>
                <td className="px-4 py-3 text-cream">{r.product_name}</td>
                <td className="px-4 py-3 font-mono text-xs text-cream/50">{r.sku}</td>
                <td className="px-4 py-3 text-cream/80">{r.color}</td>
                <td className="px-4 py-3 text-cream/80">{r.size}</td>
                <td className="px-4 py-3 font-mono text-cream/80">{money(r.price)}</td>
                <td className={`px-4 py-3 font-mono ${r.low ? "text-yellow-400" : "text-gold"}`}>{r.total}{r.low && " ⚠"}</td>
                <td className="px-4 py-3"><button onClick={() => setAdjust(r)} data-testid={`adjust-${r.variant_id}`} className="border border-white/15 px-3 py-1.5 text-[0.6rem] uppercase tracking-widest text-cream hover:border-gold hover:text-gold">Adjust</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adjust && <AdjustModal row={adjust} locations={data.locations} onClose={() => setAdjust(null)} onDone={() => { setAdjust(null); fetchData(); }} />}
    </div>
  );
}

function AdjustModal({ row, locations, onClose, onDone }) {
  const [locationId, setLocationId] = useState(locations[0]?.id || "");
  const [delta, setDelta] = useState(1);
  const [reason, setReason] = useState("Restock");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await staffApi.post("/admin/inventory/adjust", { variant_id: row.variant_id, location_id: locationId, delta: Number(delta), reason });
      toast.success(`Stock updated · new qty ${data.new_quantity}`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="adjust-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md border border-gold/25 bg-ink p-6">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-serif text-xl text-cream">Adjust Stock</h3>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} className="text-cream/60" /></button>
        </div>
        <p className="mb-4 text-sm text-cream/60">{row.product_name} · {row.color}/{row.size}</p>
        <label className="mb-3 block"><span className="overline-label mb-1 block text-[0.6rem]">Location</span>
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)} data-testid="adjust-location" className="w-full border border-white/15 bg-black px-4 py-3 text-sm text-cream outline-none focus:border-gold">
            {locations.map((l) => <option key={l.id} value={l.id} className="bg-black">{l.name}</option>)}
          </select>
        </label>
        <label className="mb-3 block"><span className="overline-label mb-1 block text-[0.6rem]">Change (+ / −)</span>
          <input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} data-testid="adjust-delta" className="w-full border border-white/15 bg-black px-4 py-3 text-sm text-cream outline-none focus:border-gold" />
        </label>
        <label className="mb-5 block"><span className="overline-label mb-1 block text-[0.6rem]">Reason</span>
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason" className="w-full border border-white/15 bg-black px-4 py-3 text-sm text-cream outline-none focus:border-gold">
            {["Restock", "Damaged", "Returned", "Correction", "Transfer", "Theft/Loss"].map((r) => <option key={r} className="bg-black">{r}</option>)}
          </select>
        </label>
        <button disabled={loading} data-testid="adjust-submit" className="w-full bg-gold py-3 text-xs font-bold uppercase tracking-widest text-black hover:bg-gold-bright disabled:opacity-50">{loading ? "Saving…" : "Apply Adjustment"}</button>
      </form>
    </div>
  );
}
